import React from "react";

import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import { Dayjs } from "dayjs";

import { ICcsInformation, IRollModel, IServiceFees } from "../../types/feeInformationFlyer";

interface ProposedFeesTableProps {
  proposedFees: IServiceFees;
  currentFees: IServiceFees | null;
  activeRolls: IRollModel[];
  isFbo: boolean;
  ccsInfo?: ICcsInformation | null;
}

export const ProposedFeesTable: React.FC<ProposedFeesTableProps> = ({ proposedFees, currentFees, activeRolls }) => {
  const formatCurrency = (amount?: number | null): string => {
    if (amount === null || amount === undefined) return "-";
    return `$${amount.toFixed(2)}`;
  };

  const formatTime = (time?: Dayjs | null): string => {
    if (!time) return "-";
    return time.format("h:mm A");
  };

  const getRollTimes = (rollClassificationId: number, careTypeId: number): string => {
    const roll = activeRolls?.find(
      (r) => r.rollClassificationId === rollClassificationId && r.govCareTypeId === careTypeId,
    );
    if (!roll) return "-";

    const startTime = roll.earliestOpenTime ? formatTime(roll.earliestOpenTime) : "";
    const endTime = roll.latestClosingTime ? formatTime(roll.latestClosingTime) : "";

    return startTime && endTime ? `${startTime} - ${endTime}` : "-";
  };

  const calculateFeeChange = (currentAmount?: number | null, proposedAmount?: number | null): string => {
    if (
      currentAmount === null ||
      currentAmount === undefined ||
      proposedAmount === null ||
      proposedAmount === undefined
    ) {
      return "-";
    }
    const change = proposedAmount - currentAmount;
    return formatCurrency(change);
  };

  return (
    <TableContainer>
      <Table>
        <TableHead>
          <TableRow sx={{ backgroundColor: "#1a5a6a" }}>
            <TableCell sx={{ color: "white", fontWeight: "bold" }}>&nbsp;</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Hours</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Current full fee</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Proposed full fee</TableCell>
            <TableCell sx={{ color: "white", fontWeight: "bold" }}>Change in full fee before CCS*</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {/* BSC Fees - Show if BscFeeId > 0 AND BscStartTime and BscEndTime exist */}
          {proposedFees.bscFeeId > 0 && proposedFees.bscStartTime && proposedFees.bscEndTime && (
            <TableRow>
              <TableCell>Before Care</TableCell>
              <TableCell>{getRollTimes(1, 3)}</TableCell>
              <TableCell>{formatCurrency(currentFees?.bscFeeAmount)}</TableCell>
              <TableCell>{formatCurrency(proposedFees.bscFeeAmount)}</TableCell>
              <TableCell>{calculateFeeChange(currentFees?.bscFeeAmount, proposedFees.bscFeeAmount)}</TableCell>
            </TableRow>
          )}

          {/* BSC ELC - Show if BscElcFeeId > 0 AND BscElcStartTime and BscElcEndTime exist */}
          {proposedFees.bscElcFeeId > 0 && proposedFees.bscElcStartTime && proposedFees.bscElcEndTime && (
            <TableRow>
              <TableCell>Before Care ELC</TableCell>
              <TableCell>{getRollTimes(2, 3)}</TableCell>
              <TableCell>{formatCurrency(currentFees?.bscElcFeeAmount)}</TableCell>
              <TableCell>{formatCurrency(proposedFees.bscElcFeeAmount)}</TableCell>
              <TableCell>{calculateFeeChange(currentFees?.bscElcFeeAmount, proposedFees.bscElcFeeAmount)}</TableCell>
            </TableRow>
          )}

          {/* ASC Fees - Show if AscFeeId > 0 AND AscStartTime and AscEndTime exist */}
          {proposedFees.ascFeeId > 0 && proposedFees.ascStartTime && proposedFees.ascEndTime && (
            <TableRow>
              <TableCell>After Care</TableCell>
              <TableCell>{getRollTimes(1, 4)}</TableCell>
              <TableCell>{formatCurrency(currentFees?.ascFeeAmount)}</TableCell>
              <TableCell>{formatCurrency(proposedFees.ascFeeAmount)}</TableCell>
              <TableCell>{calculateFeeChange(currentFees?.ascFeeAmount, proposedFees.ascFeeAmount)}</TableCell>
            </TableRow>
          )}

          {/* ASC ELC - Show if AscElcFeeId > 0 AND AscElcStartTime and AscElcEndTime exist */}
          {proposedFees.ascElcFeeId > 0 && proposedFees.ascElcStartTime && proposedFees.ascElcEndTime && (
            <TableRow>
              <TableCell>After Care ELC</TableCell>
              <TableCell>{getRollTimes(2, 4)}</TableCell>
              <TableCell>{formatCurrency(currentFees?.ascElcFeeAmount)}</TableCell>
              <TableCell>{formatCurrency(proposedFees.ascElcFeeAmount)}</TableCell>
              <TableCell>{calculateFeeChange(currentFees?.ascElcFeeAmount, proposedFees.ascElcFeeAmount)}</TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};
